import React, { useState, useEffect } from "react";
import axios from "axios";
import { API, colors, NCC_CLASSES } from "../shared/constants";
import { styles, inputStyle, labelStyle, fieldStyle } from "../shared/styles";

const sectionHeaderStyle = {
  ...labelStyle,
  fontSize: "12px",
  marginBottom: "12px",
  paddingBottom: "8px",
  borderBottom: `1px solid ${colors.border}`,
};

const checkboxLabelStyle = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: "14px",
  cursor: "pointer",
};

export function SiteFormModal({ accountId, existingSite, onClose, onSave }) {
  const isEdit = !!existingSite;
  const [step, setStep] = useState(1);
  const [siteData, setSiteData] = useState(
    isEdit
      ? {
          site_name: existingSite.site_name,
          site_address: existingSite.site_address,
          status: existingSite.status,
          notes: existingSite.notes || "",
          monthly_fee: existingSite.monthly_fee || "",
          monthly_fee_months: existingSite.monthly_fee_months || 12,
          bi_annual_fee: existingSite.bi_annual_fee || "",
          annual_fee: existingSite.annual_fee || "",
        }
      : {
          site_name: "",
          site_address: "",
          status: "active",
          notes: "",
          monthly_fee: "",
          monthly_fee_months: 12,
          bi_annual_fee: "",
          annual_fee: "",
        },
  );
  const [baselineData, setBaselineData] = useState(
    isEdit
      ? {
          levels_above_ground: existingSite.levels_above_ground ?? "",
          basement_carpark_levels: existingSite.basement_carpark_levels ?? "",
          has_outside_parking: existingSite.has_outside_parking || false,
          is_large_building: existingSite.is_large_building || false,
          has_units: existingSite.has_units || false,
          has_common_areas: existingSite.has_common_areas || false,
          has_warehouses: existingSite.has_warehouses || false,
          has_buildings: existingSite.has_buildings || false,
          under_fire_order: existingSite.under_fire_order || false,
          afss_exempt: existingSite.afss_exempt || false,
          has_monthly_service: existingSite.has_monthly_service || false,
          monthly_schedule_occurrence: existingSite.monthly_schedule_occurrence || "",
          monthly_schedule_weekday:    existingSite.monthly_schedule_weekday    || "",
          year_of_construction: existingSite.year_of_construction ?? "",
          building_classes: existingSite.building_classes
            ? existingSite.building_classes.map((bc) => bc.id)
            : [],
        }
      : {
          levels_above_ground: "",
          basement_carpark_levels: "",
          has_outside_parking: false,
          is_large_building: false,
          has_units: false,
          has_common_areas: false,
          has_warehouses: false,
          has_buildings: false,
          under_fire_order: false,
          afss_exempt: false,
          has_monthly_service: false,
          monthly_schedule_occurrence: "",
          monthly_schedule_weekday:    "",
          year_of_construction: "",
          building_classes: [],
        },
  );
  const [accessData, setAccessData] = useState(
    isEdit
      ? {
          access_instructions: existingSite.access_instructions || "",
          parking_notes: existingSite.parking_notes || "",
          tools_required: existingSite.tools_required || "",
          site_hazards: existingSite.site_hazards || "",
        }
      : {
          access_instructions: "",
          parking_notes: "",
          tools_required: "",
          site_hazards: "",
        },
  );
  const [allClasses, setAllClasses] = useState([]);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(
    isEdit && existingSite.street_view_photo_url ? existingSite.street_view_photo_url : null
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/building-classes/`)
      .then((res) => setAllClasses(res.data))
      .catch(() => {});
  }, []);

  const toggleClass = (id) => {
    setBaselineData((prev) => ({
      ...prev,
      building_classes: prev.building_classes.includes(id)
        ? prev.building_classes.filter((c) => c !== id)
        : [...prev.building_classes, id],
    }));
  };

  const handleSave = async () => {
    if (!siteData.site_name.trim()) {
      setError("Site name is required.");
      return;
    }
    if (!siteData.site_address.trim()) {
      setError("Site address is required.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      const payload = {
        account: accountId,
        site_name: siteData.site_name,
        site_address: siteData.site_address,
        status: siteData.status,
        notes: siteData.notes,
        levels_above_ground: baselineData.levels_above_ground || null,
        basement_carpark_levels: baselineData.basement_carpark_levels || null,
        has_outside_parking: baselineData.has_outside_parking,
        is_large_building: baselineData.is_large_building,
        has_units: baselineData.has_units,
        has_common_areas: baselineData.has_common_areas,
        has_warehouses: baselineData.has_warehouses,
        has_buildings: baselineData.has_buildings,
        under_fire_order: baselineData.under_fire_order,
        afss_exempt: baselineData.afss_exempt,
        has_monthly_service: baselineData.has_monthly_service,
        monthly_schedule_occurrence: baselineData.monthly_schedule_occurrence,
        monthly_schedule_weekday:    baselineData.monthly_schedule_weekday,
        year_of_construction: baselineData.year_of_construction || null,
        building_class_ids: baselineData.building_classes,
        monthly_fee: siteData.monthly_fee || null,
        monthly_fee_months: siteData.monthly_fee_months || 12,
        bi_annual_fee: siteData.bi_annual_fee || null,
        annual_fee: siteData.annual_fee || null,
        access_instructions: accessData.access_instructions,
        parking_notes: accessData.parking_notes,
        tools_required: accessData.tools_required,
        site_hazards: accessData.site_hazards,
      };
      let siteId;
      if (isEdit) {
        await axios.patch(`${API}/sites/${existingSite.id}/`, payload);
        siteId = existingSite.id;
      } else {
        const res = await axios.post(`${API}/sites/`, payload);
        siteId = res.data.id;
      }
      if (photoFile) {
        const fd = new FormData();
        fd.append("street_view_photo", photoFile);
        await axios.patch(`${API}/sites/${siteId}/`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      onSave();
      onClose();
    } catch (e) {
      setError("Something went wrong. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
      }}
    >
      <div
        style={{
          backgroundColor: colors.white,
          borderRadius: "12px",
          width: "620px",
          maxHeight: "90vh",
          overflow: "auto",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 24px",
            borderBottom: `1px solid ${colors.border}`,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ fontSize: "18px", fontWeight: "700" }}>
            {isEdit ? `Edit: ${existingSite.site_name}` : "New Site"}
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              cursor: "pointer",
              color: "#999",
            }}
          >
            ✕
          </button>
        </div>

        {/* Step indicators */}
        <div
          style={{
            display: "flex",
            padding: "16px 24px",
            gap: "8px",
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          {["Site Details", "Baseline Data", "Site Access"].map((label, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                opacity: step === i + 1 ? 1 : 0.4,
              }}
            >
              <div
                style={{
                  width: "24px",
                  height: "24px",
                  borderRadius: "50%",
                  backgroundColor: step === i + 1 ? colors.red : "#ddd",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "12px",
                  fontWeight: "700",
                }}
              >
                {i + 1}
              </div>
              <span style={{ fontSize: "13px", fontWeight: "600" }}>
                {label}
              </span>
              {i < 2 && (
                <span style={{ color: "#ddd", marginLeft: "4px" }}>→</span>
              )}
            </div>
          ))}
        </div>

        {/* Form content */}
        <div style={{ padding: "24px" }}>
          {/* Step 1 — Site Details */}
          {step === 1 && (
            <div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Site Name *</label>
                <input
                  style={inputStyle}
                  value={siteData.site_name}
                  onChange={(e) =>
                    setSiteData({ ...siteData, site_name: e.target.value })
                  }
                  placeholder="e.g. Astris PME - Ingleburn"
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Site Address *</label>
                <input
                  style={inputStyle}
                  value={siteData.site_address}
                  onChange={(e) =>
                    setSiteData({ ...siteData, site_address: e.target.value })
                  }
                  placeholder="e.g. 4 Phiney Place, Ingleburn, NSW"
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Status</label>
                <select
                  style={inputStyle}
                  value={siteData.status}
                  onChange={(e) =>
                    setSiteData({ ...siteData, status: e.target.value })
                  }
                >
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Notes</label>
                <textarea
                  style={{ ...inputStyle, height: "80px", resize: "vertical" }}
                  value={siteData.notes}
                  onChange={(e) =>
                    setSiteData({ ...siteData, notes: e.target.value })
                  }
                  placeholder="Any notes about this site..."
                />
              </div>
              <div
                style={{
                  borderTop: `1px solid ${colors.border}`,
                  paddingTop: "16px",
                  marginTop: "4px",
                }}
              >
                <div
                  style={{
                    fontSize: "12px",
                    fontWeight: "700",
                    color: "#666",
                    textTransform: "uppercase",
                    letterSpacing: "1px",
                    marginBottom: "12px",
                  }}
                >
                  Contract Fees
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "12px",
                  }}
                >
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Monthly Fee ($)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      min="0"
                      step="0.01"
                      value={siteData.monthly_fee}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          monthly_fee: e.target.value,
                        })
                      }
                      placeholder="e.g. 250.00"
                    />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Monthly × Months</label>
                    <select
                      style={inputStyle}
                      value={siteData.monthly_fee_months}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          monthly_fee_months: parseInt(e.target.value),
                        })
                      }
                    >
                      <option value={12}>12 months</option>
                      <option value={10}>10 months</option>
                    </select>
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Bi-Annual Fee ($)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      min="0"
                      step="0.01"
                      value={siteData.bi_annual_fee}
                      onChange={(e) =>
                        setSiteData({
                          ...siteData,
                          bi_annual_fee: e.target.value,
                        })
                      }
                      placeholder="e.g. 450.00"
                    />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Annual Fee ($)</label>
                    <input
                      style={inputStyle}
                      type="number"
                      min="0"
                      step="0.01"
                      value={siteData.annual_fee}
                      onChange={(e) =>
                        setSiteData({ ...siteData, annual_fee: e.target.value })
                      }
                      placeholder="e.g. 800.00"
                    />
                  </div>
                </div>
                {(siteData.monthly_fee ||
                  siteData.bi_annual_fee ||
                  siteData.annual_fee) && (
                  <div
                    style={{
                      backgroundColor: colors.lightGrey,
                      borderRadius: "6px",
                      padding: "10px 14px",
                      fontSize: "13px",
                      marginTop: "8px",
                    }}
                  >
                    <span style={{ fontWeight: "600" }}>
                      Estimated Annual Value:{" "}
                    </span>
                    <span style={{ color: colors.green, fontWeight: "700" }}>
                      $
                      {(
                        parseFloat(siteData.monthly_fee || 0) *
                          (siteData.monthly_fee_months || 12) +
                        parseFloat(siteData.bi_annual_fee || 0) +
                        parseFloat(siteData.annual_fee || 0)
                      ).toFixed(2)}
                    </span>
                  </div>
                )}
              </div>
              {error && (
                <div
                  style={{ color: "red", fontSize: "13px", marginTop: "12px" }}
                >
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Step 2 — Baseline Data */}
          {step === 2 && (
            <div>
              {/* Building Details */}
              <div style={{ marginBottom: "24px" }}>
                <label style={sectionHeaderStyle}>Building Details</label>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Levels Above Ground</label>
                    <input
                      style={inputStyle}
                      type="number"
                      min="0"
                      value={baselineData.levels_above_ground}
                      onChange={(e) =>
                        setBaselineData({
                          ...baselineData,
                          levels_above_ground: e.target.value,
                        })
                      }
                      placeholder="e.g. 5"
                    />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Basement Carpark Levels</label>
                    <input
                      style={inputStyle}
                      type="number"
                      min="0"
                      value={baselineData.basement_carpark_levels}
                      onChange={(e) =>
                        setBaselineData({
                          ...baselineData,
                          basement_carpark_levels: e.target.value,
                        })
                      }
                      placeholder="e.g. 2"
                    />
                  </div>
                  <div style={fieldStyle}>
                    <label style={labelStyle}>Year of Construction</label>
                    <input
                      style={inputStyle}
                      type="number"
                      min="1800"
                      max="2100"
                      value={baselineData.year_of_construction}
                      onChange={(e) =>
                        setBaselineData({
                          ...baselineData,
                          year_of_construction: e.target.value,
                        })
                      }
                      placeholder="e.g. 1998"
                    />
                  </div>
                  <div style={{ ...fieldStyle, display: "flex", alignItems: "center", paddingTop: "20px" }}>
                    <label style={checkboxLabelStyle}>
                      <input
                        type="checkbox"
                        checked={baselineData.has_outside_parking}
                        onChange={(e) =>
                          setBaselineData({
                            ...baselineData,
                            has_outside_parking: e.target.checked,
                          })
                        }
                      />
                      Outside Parking
                    </label>
                  </div>
                </div>
              </div>

              {/* Asset Register Structure */}
              <div style={{ marginBottom: "24px" }}>
                <label style={sectionHeaderStyle}>Asset Register Structure</label>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={baselineData.has_units}
                      onChange={(e) =>
                        setBaselineData({ ...baselineData, has_units: e.target.checked })
                      }
                    />
                    Has separate units / tenants — own tenant contact model (enables Units tab)
                  </label>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={baselineData.has_common_areas}
                      onChange={(e) => setBaselineData({ ...baselineData, has_common_areas: e.target.checked })}
                    />
                    Has common areas (enables Common Areas tab)
                  </label>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={baselineData.has_warehouses}
                      onChange={(e) => setBaselineData({ ...baselineData, has_warehouses: e.target.checked })}
                    />
                    Has warehouses (enables Warehouses tab)
                  </label>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={baselineData.has_buildings}
                      onChange={(e) => setBaselineData({ ...baselineData, has_buildings: e.target.checked })}
                    />
                    Has separate buildings (enables Buildings tab)
                  </label>
                </div>
              </div>

              {/* Compliance & Scheduling */}
              <div style={{ marginBottom: "24px" }}>
                <label style={sectionHeaderStyle}>Compliance &amp; Scheduling</label>
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={baselineData.is_large_building}
                      onChange={(e) =>
                        setBaselineData({
                          ...baselineData,
                          is_large_building: e.target.checked,
                        })
                      }
                    />
                    Large Building — individual EFSM reports &amp; quotes
                  </label>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={baselineData.under_fire_order}
                      onChange={(e) => setBaselineData({ ...baselineData, under_fire_order: e.target.checked })}
                    />
                    Under Fire Order — excluded from AFSS tracking
                  </label>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={baselineData.afss_exempt}
                      onChange={(e) => setBaselineData({ ...baselineData, afss_exempt: e.target.checked })}
                    />
                    AFSS Exempt — excluded from AFSS requirements
                  </label>
                  <label style={checkboxLabelStyle}>
                    <input
                      type="checkbox"
                      checked={baselineData.has_monthly_service}
                      onChange={(e) => setBaselineData({ ...baselineData, has_monthly_service: e.target.checked })}
                    />
                    Monthly Service — included in monthly service tracking
                  </label>
                  <div style={{ marginTop: "4px", marginLeft: "24px" }}>
                    <label style={labelStyle}>Monthly Service Schedule</label>
                    <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                      <select
                        value={baselineData.monthly_schedule_occurrence}
                        onChange={(e) => setBaselineData({ ...baselineData, monthly_schedule_occurrence: e.target.value })}
                        style={{ ...inputStyle, flex: 1 }}
                      >
                        <option value="">— Occurrence —</option>
                        <option value="first">First</option>
                        <option value="second">Second</option>
                        <option value="third">Third</option>
                        <option value="fourth">Fourth</option>
                        <option value="last">Last</option>
                      </select>
                      <select
                        value={baselineData.monthly_schedule_weekday}
                        onChange={(e) => setBaselineData({ ...baselineData, monthly_schedule_weekday: e.target.value })}
                        style={{ ...inputStyle, flex: 1 }}
                      >
                        <option value="">— Day —</option>
                        <option value="monday">Monday</option>
                        <option value="tuesday">Tuesday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="thursday">Thursday</option>
                        <option value="friday">Friday</option>
                        <option value="saturday">Saturday</option>
                        <option value="sunday">Sunday</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <div style={fieldStyle}>
                <label style={labelStyle}>Building Classes</label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "8px",
                    maxHeight: "240px",
                    overflow: "auto",
                    border: `1px solid ${colors.border}`,
                    borderRadius: "6px",
                    padding: "12px",
                  }}
                >
                  {allClasses.map((bc) => (
                    <label
                      key={bc.id}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                        fontSize: "13px",
                        cursor: "pointer",
                        padding: "4px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={baselineData.building_classes.includes(bc.id)}
                        onChange={() => toggleClass(bc.id)}
                        style={{ marginTop: "2px", flexShrink: 0 }}
                      />
                      <span>
                        {NCC_CLASSES.find((c) => c.code === bc.code)?.label ||
                          bc.code}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              {error && (
                <div
                  style={{ color: "red", fontSize: "13px", marginTop: "12px" }}
                >
                  {error}
                </div>
              )}
            </div>
          )}

          {/* Step 3 — Site Access */}
          {step === 3 && (
            <div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Access Instructions</label>
                <textarea
                  style={{ ...inputStyle, height: "80px", resize: "vertical" }}
                  value={accessData.access_instructions}
                  onChange={(e) =>
                    setAccessData({
                      ...accessData,
                      access_instructions: e.target.value,
                    })
                  }
                  placeholder="e.g. Intercom unit 12, key safe code 1234, report to concierge..."
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Parking Notes</label>
                <textarea
                  style={{ ...inputStyle, height: "70px", resize: "vertical" }}
                  value={accessData.parking_notes}
                  onChange={(e) =>
                    setAccessData({
                      ...accessData,
                      parking_notes: e.target.value,
                    })
                  }
                  placeholder="e.g. Street parking on Smith St, loading dock available..."
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Tools / Materials Required</label>
                <textarea
                  style={{ ...inputStyle, height: "70px", resize: "vertical" }}
                  value={accessData.tools_required}
                  onChange={(e) =>
                    setAccessData({
                      ...accessData,
                      tools_required: e.target.value,
                    })
                  }
                  placeholder="e.g. Bring 6kg extinguisher refills, ladder required..."
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>⚠️ Site Hazards</label>
                <textarea
                  style={{
                    ...inputStyle,
                    height: "70px",
                    resize: "vertical",
                    borderColor: accessData.site_hazards
                      ? colors.red
                      : colors.border,
                  }}
                  value={accessData.site_hazards}
                  onChange={(e) =>
                    setAccessData({
                      ...accessData,
                      site_hazards: e.target.value,
                    })
                  }
                  placeholder="e.g. Aggressive dog on premises, asbestos in ceiling..."
                />
              </div>
              <div style={fieldStyle}>
                <label style={labelStyle}>Street View Photo (optional)</label>
                {photoPreview && (
                  <div style={{ marginBottom: "8px", position: "relative", display: "inline-block" }}>
                    <img
                      src={photoPreview}
                      alt="Street view"
                      style={{ width: "100%", maxHeight: "180px", objectFit: "cover", borderRadius: "8px", border: `1px solid ${colors.border}` }}
                    />
                    <button
                      onClick={() => { setPhotoFile(null); setPhotoPreview(null); }}
                      style={{ position: "absolute", top: "6px", right: "6px", background: "rgba(0,0,0,0.5)", color: "white", border: "none", borderRadius: "50%", width: "24px", height: "24px", cursor: "pointer", fontSize: "14px", lineHeight: "24px", textAlign: "center", padding: 0 }}
                    >
                      ✕
                    </button>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  style={{ fontSize: "13px", color: colors.slate }}
                  onChange={(e) => {
                    const f = e.target.files[0];
                    if (!f) return;
                    setPhotoFile(f);
                    setPhotoPreview(URL.createObjectURL(f));
                  }}
                />
                <div style={{ fontSize: "12px", color: colors.slate, marginTop: "4px" }}>
                  A photo of the building exterior to help techs identify the site.
                </div>
              </div>
              {error && (
                <div
                  style={{ color: "red", fontSize: "13px", marginTop: "12px" }}
                >
                  {error}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: `1px solid ${colors.border}`,
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <button
            onClick={step === 1 ? onClose : () => setStep(step - 1)}
            style={{
              background: "none",
              border: `1px solid ${colors.border}`,
              borderRadius: "6px",
              padding: "8px 20px",
              cursor: "pointer",
              fontSize: "14px",
            }}
          >
            {step === 1 ? "Cancel" : "← Back"}
          </button>

          {step === 1 && (
            <button
              onClick={() => {
                if (!siteData.site_name.trim()) {
                  setError("Site name is required.");
                  return;
                }
                if (!siteData.site_address.trim()) {
                  setError("Site address is required.");
                  return;
                }
                setError("");
                setStep(2);
              }}
              style={styles.addButton}
            >
              Next: Baseline Data →
            </button>
          )}
          {step === 2 && (
            <button
              onClick={() => {
                setError("");
                setStep(3);
              }}
              style={styles.addButton}
            >
              Next: Site Access →
            </button>
          )}
          {step === 3 && (
            <button
              onClick={handleSave}
              disabled={saving}
              style={styles.addButton}
            >
              {saving ? "Saving..." : isEdit ? "Save Changes" : "Save Site"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
